import { useState, useCallback } from 'react';
import type { ToastMessage } from '../components/ui/Toast';

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((
    message: string,
    type: ToastMessage['type'] = 'info',
    duration?: number
  ) => {
    const id = Math.random().toString(36).substring(2, 11);
    const toast: ToastMessage = {
      id,
      message,
      type,
      duration
    };

    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message: string, duration?: number) => 
    addToast(message, 'success', duration), [addToast]);

  const error = useCallback((message: string, duration?: number) => 
    addToast(message, 'error', duration), [addToast]);

  const warning = useCallback((message: string, duration?: number) => 
    addToast(message, 'warning', duration), [addToast]);

  const info = useCallback((message: string, duration?: number) => 
    addToast(message, 'info', duration), [addToast]);

  // Alias for addToast to match component expectations
  const showToast = useCallback((message: string, type: ToastMessage['type'] = 'info', duration?: number) => {
    return addToast(message, type, duration);
  }, [addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showToast,
    success,
    error,
    warning,
    info
  };
};